/**
 * @fileoverview App Header Component
 *
 * Provides a mobile-first header/toolbar for user applications with
 * sidebar toggle, title, notifications, and user menu.
 *
 * @module layout/app/AppHeader
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, FlexibleIcon } from '@xanectech/ui/components';
import { Avatar, AvatarFallback, AvatarImage } from '@xanectech/ui/components';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@xanectech/ui/components';
import { SidebarTrigger, useSidebar } from '@xanectech/ui/components';
import { Bell, LogOut, User, Settings } from 'lucide-react';
import { cn } from '@xanectech/ui/lib';
import { AppHeaderProps } from './app-config';
import { useAuth } from '@xanectech/auth';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Components - AppHeader');

interface UserMetadata {
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
}

const getUserMetadata = (value: unknown): UserMetadata => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const metadata = value as Record<string, unknown>;
  return {
    first_name:
      typeof metadata.first_name === 'string' ? metadata.first_name : undefined,
    last_name:
      typeof metadata.last_name === 'string' ? metadata.last_name : undefined,
    avatar_url:
      typeof metadata.avatar_url === 'string' ? metadata.avatar_url : undefined,
  };
};

const isHeaderComponentType = (
  value: React.ReactNode | React.ComponentType | undefined,
): value is React.ComponentType<Record<string, never>> =>
  typeof value === 'function';

/**
 * AppHeader Component
 *
 * Mobile-first header with:
 * - Sidebar toggle button with logo
 * - Page title
 * - Notifications bell (optional)
 * - User avatar menu (optional)
 * - Hide on scroll down, show on scroll up
 * - Sticky positioning
 *
 * @component
 * @example
 * ```tsx
 * <AppHeader
 *   title="Dashboard"
 *   showNotifications={true}
 *   showUserMenu={true}
 * />
 * ```
 */
export const AppHeader: React.FC<AppHeaderProps> = ({
  title = 'App',
  icon,
  showSidebarToggle = true,
  showNotifications = true,
  showUserMenu = true,
  notifications = [],
  appHeaderComponent,
  onLogout,
}) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { toggleSidebar } = useSidebar();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const userMetadata = getUserMetadata(user?.user_metadata);

  // Handle scroll to show/hide header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when scrolling up or at top
      if (currentScrollY < lastScrollY || currentScrollY < 10) {
        setIsVisible(true);
      }
      // Hide header when scrolling down (past threshold)
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleLogout = async () => {
    if (onLogout) {
      onLogout();
      return;
    }

    await signOut();
    void navigate('/logout');
  };

  const getUserInitials = () => {
    if (userMetadata.first_name && userMetadata.last_name) {
      return `${userMetadata.first_name.charAt(0)}${userMetadata.last_name.charAt(0)}`;
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const getUserDisplayName = () => {
    if (userMetadata.first_name && userMetadata.last_name) {
      return `${userMetadata.first_name} ${userMetadata.last_name}`;
    }
    return user?.email || 'User';
  };

  const unreadNotifications = (notifications ?? []).filter(
    (notification) => !notification.read,
  );
  const topNotifications = (notifications ?? []).slice(0, 5);

  return (
    <header
      className={cn(
        'sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
        'transition-transform duration-300',
        isVisible ? 'translate-y-0' : '-translate-y-full',
      )}
    >
      <div className='flex h-14 items-center justify-between px-4 lg:px-6'>
        {/* Left section: Sidebar toggle and title */}
        <div className='flex items-center gap-3 flex-1 min-w-0'>
          {showSidebarToggle && (
            <div className='flex items-center gap-2'>
              <SidebarTrigger className='text-primary' />
            </div>
          )}
          <h1 className='text-lg text-primary font-semibold truncate'>
            {title}
          </h1>
          {/* {icon && (
            <FlexibleIcon icon={icon} size={20} className='text-primary' />
          )} */}
        </div>

        {/* Right section: Notifications and user menu */}
        <div className='flex items-center gap-2'>
          {appHeaderComponent && (
            <div>
              {isHeaderComponentType(appHeaderComponent)
                ? React.createElement(appHeaderComponent)
                : appHeaderComponent}
            </div>
          )}
          {showNotifications && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='relative'
                  aria-label='Notifications'
                >
                  <Bell className='h-5 w-5' />
                  {unreadNotifications.length > 0 && (
                    <span className='absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-[10px] font-bold text-destructive-foreground'>
                      {unreadNotifications.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='w-80'>
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {topNotifications.length === 0 ? (
                  <DropdownMenuItem disabled>No notifications</DropdownMenuItem>
                ) : (
                  topNotifications.map((notification) => (
                    <DropdownMenuItem
                      key={notification.id}
                      className='flex flex-col items-start gap-1 py-3'
                    >
                      <span className='font-medium'>{notification.title}</span>
                      {notification.description && (
                        <span className='text-sm text-muted-foreground'>
                          {notification.description}
                        </span>
                      )}
                      <span className='text-xs text-muted-foreground'>
                        {notification.timestamp}
                      </span>
                    </DropdownMenuItem>
                  ))
                )}
                {notifications.length > 5 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className='justify-center text-primary'>
                      View all notifications
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {showUserMenu && user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  className='relative h-8 w-8 rounded-full'
                  aria-label='User menu'
                >
                  <Avatar className='h-8 w-8'>
                    <AvatarImage
                      src={userMetadata.avatar_url}
                      alt={getUserDisplayName()}
                    />
                    <AvatarFallback className='bg-primary text-primary-foreground text-xs'>
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='w-56' align='end' forceMount>
                <DropdownMenuLabel className='font-normal'>
                  <div className='flex flex-col space-y-1'>
                    <p className='text-sm font-medium leading-none'>
                      {getUserDisplayName()}
                    </p>
                    <p className='text-xs leading-none text-muted-foreground'>
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className='mr-2 h-4 w-4' />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className='mr-2 h-4 w-4' />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className='text-destructive focus:text-destructive focus:bg-destructive/10'
                >
                  <LogOut className='mr-2 h-4 w-4' />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};
